import { useEffect, useRef, useState } from 'react'

export type SelectOption = { value: string; label: string; sub?: string }

/**
 * A searchable dropdown (combobox): a styled trigger that opens a filterable list. Drop-in
 * replacement for a native <select> anywhere the option list can grow — domains, pools,
 * nodes, plans. Keyboard: type to filter, ↑/↓ to move, Enter to pick, Esc to close.
 */
export function SearchableSelect({
  value, onChange, options, placeholder = 'Select…', searchPlaceholder = 'Search…',
  disabled = false, className = '',
}: {
  value: string
  onChange: (v: string) => void
  options: SelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = options.find(o => o.value === value)
  const needle = q.trim().toLowerCase()
  const filtered = needle
    ? options.filter(o => `${o.label} ${o.sub ?? ''}`.toLowerCase().includes(needle))
    : options

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => {
    if (open) { setQ(''); setActive(0); setTimeout(() => inputRef.current?.focus(), 0) }
  }, [open])

  function choose(v: string) { onChange(v); setOpen(false) }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) choose(filtered[active].value) }
    else if (e.key === 'Escape') { setOpen(false) }
  }

  return (
    <div className={`ss ${open ? 'open' : ''} ${className}`} ref={wrapRef}>
      <button type="button" className="ss-trigger" disabled={disabled}
              onClick={() => !disabled && setOpen(o => !o)}
              aria-haspopup="listbox" aria-expanded={open}>
        <span className={selected ? 'ss-val' : 'ss-ph'}>{selected ? selected.label : placeholder}</span>
        <svg className="ss-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>

      {open && (
        <div className="ss-panel" role="listbox">
          <div className="ss-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
            <input ref={inputRef} placeholder={searchPlaceholder} value={q}
                   onChange={e => { setQ(e.target.value); setActive(0) }} onKeyDown={onKey} />
          </div>
          <div className="ss-list">
            {filtered.length === 0 && <div className="ss-empty">No matches</div>}
            {filtered.map((o, i) => (
              <button type="button" key={o.value} role="option" aria-selected={o.value === value}
                      className={`ss-opt ${o.value === value ? 'sel' : ''} ${i === active ? 'active' : ''}`}
                      onMouseEnter={() => setActive(i)} onClick={() => choose(o.value)}>
                <span className="ss-opt-main">
                  {o.label}
                  {o.sub && <span className="ss-sub">{o.sub}</span>}
                </span>
                {o.value === value && (
                  <svg className="ss-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
